import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, Route, Routes, useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCOUNT_TABS} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {
    AssignUsersEntitlements,
    DischargeUsersEntitlements,
} from 'src/modules/bulk-update/update-users-entitlements/select-entitlements-step';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {AccountPageStructure} from 'src/modules/profile/account/components/account-page-structure';
import {ValidatedCreateLicensePage} from 'src/modules/profile/account/create-license/';
import {clearAccountProfileTab} from 'src/modules/profile/account/tabs/account-profile/services/account-profile-slice';
import {AssignAccountEntitlements} from 'src/modules/profile/account/tabs/assign-entilements/assign-account-entitlements';
import {AssignProductAccountEntitlements} from 'src/modules/profile/account/tabs/assign-product-entilements';
import {AccountAuditLog} from 'src/modules/profile/account/tabs/audit-log/audit-log';
import {getActiveAccountProfileLicense} from 'src/openapi/admin/apis/AccountActiveLicenseApi';
import {getAccountHierarchyTree} from 'src/openapi/admin/apis/AccountHierarchyApi';
import {useAppDispatch} from 'src/store/hooks';

import Loading from '@pb/kit-component-loading';

import {AccountPageHeader} from './components/account-page-header';
import {CreateUserPage} from './create-user';
import {clearAccountPage, selectAccountDetails} from './services/account-page-slice';
import {AccountProfileTab} from './tabs/account-profile';
import {MicroApp as ApiTab} from './tabs/admin-account-api-v2';
import {Conference} from './tabs/confenrence';
import {AccountDetailsTab} from './tabs/details';
import {EntitlementsWrapper} from './tabs/entitlements';
import {LicenseFeaturesTab} from './tabs/license-and-features';
import {
    clearAccountLicense,
    clearAccountLicenseHistory,
} from './tabs/license-and-features/services/account-license-features-slice';
import {McpTab} from './tabs/mcp';
import {clearMcpData} from './tabs/mcp/services/mcp-slice';
import {MicrositesRouter} from './tabs/microsites';
import {ProductAccessTab} from './tabs/product-access';
import {AccountSettingsTab} from './tabs/settings';
import {UsersGroupsTab} from './tabs/users-and-groups';

import type {Error} from 'src/openapi/admin/models/Error';

export const AccountPage = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const queryParams = useParams<{accountId?: string, activeTab?: string}>();
    const accountId = queryParams?.accountId ? +queryParams.accountId : -1;

    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const {hierarchy} = useSelector(selectAccountDetails);

    const {
        hasAccess,
        canCreateUser,
        canCreateLicense,
        canEditAccountEntitlements,
        canEditUserEntitlements,
        canViewAccountMcp,
        canEditAccountMcp,
        canViewAccountProducts,
    } = useAdminAccessRight();
    const isMcpTabAvailable = canViewAccountMcp(accountId);
    const {license: activeLicense} = useSelector(selectAccountDetails);

    const clearAccountPageData = useCallback(() => {
        dispatch(clearAccountPage());
        dispatch(clearAccountProfileTab());
        dispatch(clearAccountLicense());
        dispatch(clearAccountLicenseHistory());
        dispatch(clearMcpData());
    }, [dispatch]);

    useEffect(() => {
        clearAccountPageData();
        const loadAccountHierarchy = async () => {
            setIsLoading(true);
            const accountHierarchyResponse = await apiDispatch(getAccountHierarchyTree({accountId}));
            setIsLoading(false);

            if (accountHierarchyResponse.payload.error) {
                setLoadingError(accountHierarchyResponse.payload.data);
            }
        };

        loadAccountHierarchy();
    }, [accountId, apiDispatch, clearAccountPageData]);

    useEffect(() => {
        if (!activeLicense && accountId) {
            apiDispatch(getActiveAccountProfileLicense({accountId}));
        }
    }, [activeLicense, accountId, apiDispatch]);

    useEffect(() => {
        return clearAccountPageData;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isUntracked = hierarchy?.current?.isUntracked ?? true;
    const isUserCreationAvailable = useMemo(() => canCreateUser(accountId), [accountId, canCreateUser]);
    const isLicenseCreationAvailable = canCreateLicense(accountId);
    const isApiTabAvailable = hasAccess(ACCESS_RIGHTS.API_V2_SCREEN_VIEW);
    const isMicroSiteTabAvailable = hasAccess(ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW);

    const isAccountEntitlementsViewAvailable = hasAccess(ACCESS_RIGHTS.ACCOUNT_ENTITLEMENTS_VIEW);
    const isAccountEntitlementsEditAvailable = canEditAccountEntitlements(accountId);
    const isConferenceAccount = useConferenceAccountCheck();
    const isConferenceViewAllowed = hasAccess(ACCESS_RIGHTS.CONFERENCE_VIEW);

    const canViewProductAllow = useMemo(
        () => canViewAccountProducts(accountId),
        [canViewAccountProducts, accountId],
    );

    return (
        <Skeleton shown={isLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler serverError={loadingError} sectionName="Account Page">
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    {hierarchy ? (
                        <AccountPageHeader
                            id={accountId}
                            hierarchy={hierarchy}
                            onChangeAccount={() => apiDispatch(getAccountHierarchyTree({accountId}))}
                        />
                    ) : null}
                    <Routes>
                        <Route
                            path={`/${ACCOUNT_TABS.PROFILE.name}`}
                            element={(
                                <AccountPageStructure
                                    accountId={accountId}
                                    activeTab={ACCOUNT_TABS.PROFILE.name}
                                    showSidebar={true}>
                                    <AccountProfileTab accountId={accountId}/>
                                </AccountPageStructure>
                            )}
                        />

                        <Route
                            path={`/${ACCOUNT_TABS.DETAILS.name}`}
                            element={(
                                <AccountPageStructure
                                    accountId={accountId}
                                    activeTab={ACCOUNT_TABS.DETAILS.name}
                                    showSidebar={true}>
                                    <AccountDetailsTab accountId={accountId}/>
                                </AccountPageStructure>
                            )}
                        />

                        <Route
                            path={`/${ACCOUNT_TABS.USERS.name}/*`}
                            element={(
                                <AccountPageStructure
                                    accountId={accountId}
                                    activeTab={ACCOUNT_TABS.USERS.name}>
                                    <UsersGroupsTab accountId={accountId}/>
                                </AccountPageStructure>
                            )}
                        />

                        {isConferenceAccount && isConferenceViewAllowed ? (
                            <Route
                                path={`/${ACCOUNT_TABS.CONFERENCE.name}/*`}
                                element={
                                    <Conference accountId={accountId}/>
                                }
                            />
                        ) : null}

                        <Route
                            path={`/${ACCOUNT_TABS.LICENSE.name}/*`}
                            element={(
                                <AccountPageStructure
                                    accountId={accountId}
                                    activeTab={ACCOUNT_TABS.LICENSE.name}>
                                    <LicenseFeaturesTab accountId={accountId}/>
                                </AccountPageStructure>
                            )}
                        />

                        {canViewProductAllow ? (
                            <Route
                                path={`/${ACCOUNT_TABS.PRODUCT_ACCESS.name}`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.PRODUCT_ACCESS.name}>
                                        <ProductAccessTab accountId={accountId}/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isApiTabAvailable && !isUntracked ? (
                            <Route
                                path={`/${ACCOUNT_TABS.API.name}/*`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.API.name}
                                        showSidebar={true}>
                                        <ApiTab accountId={accountId}/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isMcpTabAvailable ? (
                            <Route
                                path={`/${ACCOUNT_TABS.MCP.name}/*`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.MCP.name}
                                        showSidebar={true}>
                                        <McpTab accountId={accountId}/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        <Route
                            path={`/${ACCOUNT_TABS.SETTINGS.name}/*`}
                            element={(
                                <AccountPageStructure
                                    accountId={accountId}
                                    activeTab={ACCOUNT_TABS.SETTINGS.name}
                                    showSidebar={true}>
                                    <AccountSettingsTab accountId={+accountId}/>
                                </AccountPageStructure>
                            )}
                        />
                        <Route
                            path={`/${ACCOUNT_TABS.AUDIT_LOG.name}/*`}
                            element={(
                                <AccountPageStructure
                                    accountId={accountId}
                                    activeTab={ACCOUNT_TABS.AUDIT_LOG.name}
                                    showSidebar={false}>
                                    <AccountAuditLog accountId={+accountId}/>
                                </AccountPageStructure>
                            )}
                        />

                        {isAccountEntitlementsViewAvailable ? (
                            <Route
                                path={`/${ACCOUNT_TABS.ENTITLEMENTS.name}/*`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.ENTITLEMENTS.name}>
                                        <EntitlementsWrapper accountId={accountId}/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isLicenseCreationAvailable ? (
                            <Route
                                path={`/${ACCOUNT_TABS.CREATE_LICENSE.name}/:templateLicenseId?`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.CREATE_LICENSE.name}>
                                        <ValidatedCreateLicensePage/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isUserCreationAvailable && !isConferenceAccount ? (
                            <Route
                                path={`/${ACCOUNT_TABS.CREATE_USER.name}`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.CREATE_USER.name}>
                                        <CreateUserPage/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isAccountEntitlementsEditAvailable ? (
                            <Route
                                path={`/${ACCOUNT_TABS.ASSIGN_ENTITLEMENTS.name}`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.ASSIGN_ENTITLEMENTS.name}>
                                        <AssignAccountEntitlements/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isMcpTabAvailable && canEditAccountMcp(accountId) ? (
                            <Route
                                path="/assign-product-resources"
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.ENTITLEMENTS.name}>
                                        <AssignProductAccountEntitlements/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {canEditUserEntitlements(accountId) ? (
                            <Route
                                path={`/${ACCOUNT_TABS.ASSIGN_USERS_ENTITLEMENTS.name}`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.ASSIGN_USERS_ENTITLEMENTS.name}>
                                        <AssignUsersEntitlements/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {canEditUserEntitlements(accountId) ? (
                            <Route
                                path={`/${ACCOUNT_TABS.DISCHARGE_USERS_ENTITLEMENTS.name}`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        activeTab={ACCOUNT_TABS.DISCHARGE_USERS_ENTITLEMENTS.name}>
                                        <DischargeUsersEntitlements/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        {isMicroSiteTabAvailable ? (
                            <Route
                                path={`/${ACCOUNT_TABS.MICRO_SITES.name}/*`}
                                element={(
                                    <AccountPageStructure
                                        accountId={accountId}
                                        showSidebar={true}
                                        activeTab={ACCOUNT_TABS.MICRO_SITES.name}>
                                        <MicrositesRouter accountId={accountId}/>
                                    </AccountPageStructure>
                                )}
                            />
                        ) : null}

                        <Route
                            path="*"
                            element={(
                                <Navigate
                                    to={`/profile/account/${accountId}/${ACCOUNT_TABS.PROFILE.name}`}
                                    replace={true}
                                />
                            )}
                        />
                    </Routes>
                </ErrorBoundary>
            </ServerResponseHandler>
        </Skeleton>
    );
};
